<template>
  <v-dialog v-model="dialog" max-width="800px" scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-btn 
        small 
        outlined 
        color="primary"
        v-bind="attrs" 
        v-on="on">
        <v-icon left small>mdi-plus</v-icon>
        Select Property
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <h3>Select Property for "{{ columnHeader }}"</h3>
          <p class="text-caption mb-0 text--secondary">
            Choose from {{ tagType }} properties
          </p>
        </div>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Search Bar -->
      <v-card-text class="pb-0">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search properties..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          class="mb-3"
        />
      </v-card-text>

      <!-- Match Found Section -->
      <v-card-text v-if="exactMatch" class="pt-0">
        <v-alert 
          :type="exactMatch.type === 'exact' ? 'success' : 'info'" 
          dense 
          outlined>
          <div class="d-flex align-center">
            <v-icon class="mr-2">
              {{ exactMatch.type === 'exact' ? 'mdi-check-circle' : 'mdi-map-marker' }}
            </v-icon>
            <span class="font-weight-medium">
              {{ exactMatch.type === 'exact' ? 'Exact Match Found' : 'Common Mapping Found' }}
            </span>
          </div>
        </v-alert>
        
        <v-list dense>
          <v-list-item @click="selectProperty(exactMatch.property)">
            <v-list-item-avatar>
              <v-icon :color="exactMatch.type === 'exact' ? 'success' : 'info'">
                {{ exactMatch.type === 'exact' ? 'mdi-target' : 'mdi-map' }}
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ exactMatch.title }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ exactMatch.property }}
                <v-chip v-if="exactMatch.type === 'common'" x-small color="info" class="ml-2">
                  Common Mapping
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon>mdi-arrow-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        
        <v-divider class="my-3" />
      </v-card-text>

      <!-- All Properties -->
      <v-card-text class="pt-0" style="max-height: 400px; overflow-y: auto;">
        <v-list dense>
          <v-list-item
            v-for="property in filteredProperties"
            :key="property.property"
            @click="selectProperty(property.property)"
            :class="{ 'v-list-item--active': selectedProperty === property.property }">
            <v-list-item-content>
              <v-list-item-title>{{ property.title }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ property.property }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon v-if="isPropertyMapped(property.property)" color="warning">
                mdi-alert
              </v-icon>
              <v-icon v-else>mdi-arrow-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn @click="dialog = false">Cancel</v-btn>
        <v-spacer />
        <v-btn 
          color="primary" 
          @click="selectProperty(selectedProperty)"
          :disabled="!selectedProperty">
          Map Property
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import forgePropertyMapper from '../utils/forgePropertyMapper';

export default {
  name: 'PropertySelector',
  props: {
    columnHeader: {
      type: String,
      required: true
    },
    tagType: {
      type: String,
      required: true
    },
    existingMappings: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialog: false,
      searchQuery: '',
      selectedProperty: null,
      allProperties: [],
      exactMatch: null
    };
  },
  computed: {
    filteredProperties() {
      if (!this.searchQuery) {
        return this.allProperties;
      }

      const query = this.searchQuery.toLowerCase();
      return this.allProperties.filter(prop => 
        prop.title.toLowerCase().includes(query) ||
        prop.property.toLowerCase().includes(query)
      );
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.loadProperties();
        this.findExactMatch();
      }
    }
  },
  methods: {
    loadProperties() {
      this.allProperties = forgePropertyMapper.getAvailableProperties(this.tagType);
    },

    findExactMatch() {
      const exactMatches = forgePropertyMapper.getExactMatches([this.columnHeader], this.tagType);
      this.exactMatch = exactMatches.length > 0 ? exactMatches[0] : null;
    },

    selectProperty(property) {
      if (!property) return;
      
      this.selectedProperty = property;
      this.$emit('property-selected', {
        columnHeader: this.columnHeader,
        property: property
      });
      this.dialog = false;
    },

    isPropertyMapped(property) {
      return Object.values(this.existingMappings).includes(property);
    }
  }
};
</script>

<style scoped>
.v-list-item--active {
  background-color: rgba(var(--v-primary-base), 0.1) !important;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
